import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Alert, Form, Input, Typography } from 'antd';

import { Button } from '@/components/Button';
import { ROUTES } from '@/routes';
import { getAndClearRedirectPath, parseClerkError } from '@/utils';
import styles from './Login.module.scss';

interface LoginFormValues {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, setActive, isLoaded } = useSignIn();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set by the buy-link completion redirect: the invitation was emailed to the
  // address entered at checkout (we never hand the acceptance ticket to this
  // browser). Nudge the buyer to open it.
  const inviteSent = searchParams.get('status') === 'invite_sent';

  const handleSubmit = async (values: LoginFormValues) => {
    if (!signIn || !isLoaded) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await signIn.create({
        identifier: values.email,
        password: values.password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        const redirectUrl = getAndClearRedirectPath() ?? ROUTES.SESSIONS;
        navigate(redirectUrl, { replace: true });
      } else {
        const emailFactor = result.supportedFirstFactors?.find(
          (f) => f.strategy === 'email_code'
        );
        if (emailFactor && 'emailAddressId' in emailFactor) {
          await signIn.prepareFirstFactor({
            strategy: 'email_code',
            emailAddressId: emailFactor.emailAddressId,
          });
          navigate(ROUTES.VERIFY_DEVICE);
        } else {
          setError('Additional verification required. Please contact support.');
        }
      }
    } catch (err) {
      setError(parseClerkError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Typography.Title level={1} className={styles.title}>
        Welcome back
      </Typography.Title>

      {inviteSent && (
        <Alert
          message="Check your email"
          description="We've sent an invitation to the email used at checkout. Open it to finish setting up your account and access your purchase."
          type="success"
          showIcon
          className={styles.alert}
        />
      )}

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.alert}
        />
      )}

      <Form<LoginFormValues>
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="on"
        requiredMark={false}
        colon={false}
        className={styles.form}
        initialValues={{ email: '', password: '' }}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            placeholder="you@example.com"
            size="large"
            autoComplete="email"
            disabled={isSubmitting}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <div className={styles.rowLabel}>
              <span>Password</span>
              <Link to={ROUTES.RESET_PASSWORD} className={styles.forgotLink}>
                Forgot password?
              </Link>
            </div>
          }
          rules={[{ required: true, message: 'Please enter your password' }]}
        >
          <Input.Password
            placeholder="••••••••"
            size="large"
            autoComplete="current-password"
            disabled={isSubmitting}
            iconRender={(visible) => (visible ? 'Hide' : 'Show')}
          />
        </Form.Item>

        <Button
          appearance="solid"
          htmlType="submit"
          size="large"
          block
          loading={isSubmitting}
          disabled={!isLoaded}
        >
          Login
        </Button>
      </Form>
    </div>
  );
};

export default Login;
